import { cn } from "@newxsystems/shared";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  headingEffect,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          headingEffect={headingEffect}
          reveal
        />
        {/* One raised surface for every card. The old block tinted each card from
            a four-colour rotation, which read as four unrelated brands rather
            than one grid; the accent lives in the quote mark instead. */}
        <div
          className={cn("grid grid-cols-1 gap-5 sm:grid-cols-2", columns[columnCount || 3])}
          data-reveal="stagger"
        >
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "flex min-h-56 flex-col justify-between gap-8 rounded-[14px] border border-border bg-surface-200 p-4 transition-colors duration-200 hover:border-primary/40 md:p-6",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <blockquote className="flex flex-col gap-5">
                <span
                  aria-hidden="true"
                  className="font-daylight-display text-4xl leading-[0.6] text-primary"
                >
                  “
                </span>
                <DaylightRichText
                  className="max-w-[44ch] font-daylight-display text-xl font-semibold leading-[1.3] tracking-[-0.02em] text-foreground md:text-2xl"
                  content={item.quote}
                />
              </blockquote>
              <div className="flex items-center gap-4 border-t border-border pt-5">
                {item.portrait ? (
                  <DaylightMedia
                    aspectRatio="1:1"
                    className="size-12 shrink-0"
                    media={item.portrait.image}
                    effect={item.portrait.effect}
                    sizes="48px"
                  />
                ) : null}
                <div className="min-w-0">
                  <p className="font-daylight-body text-sm font-semibold text-foreground">
                    {item.name}
                  </p>
                  {item.attribution ? (
                    <p className="mt-1 font-daylight-mono text-xs text-ash">{item.attribution}</p>
                  ) : null}
                </div>
              </div>
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
